import { useMemo } from "react";
import { XAxisPlotBandsOptions, XAxisPlotLinesOptions } from "highcharts";

import COLORS from "@/features/shared/widgets/graphs/colors";
import { interpolateIntersection } from "@/features/shared/widgets/graphs/helpers";
import { LineGraphWidgetConfiguration, LineGraphWidgetData } from "@/lib/api";

export const useThresholdsX = (
  configuration: LineGraphWidgetConfiguration,
  data?: LineGraphWidgetData,
) => {
  return useMemo<[XAxisPlotBandsOptions[], XAxisPlotLinesOptions[]]>(() => {
    if (!configuration.thresholds) return [[], []];
    if (!data?.lines) return [[], []];

    const plots: XAxisPlotBandsOptions[] = [];
    const lines: XAxisPlotLinesOptions[] = [];
    let currentPlot: XAxisPlotBandsOptions | undefined = undefined;
    const thresholds = configuration.thresholds.sort((a, b) => {
      // sort ascending for max threshold
      // sort descending for min thresholds
      if (a.threshold_type === b.threshold_type) {
        if (a.threshold_type === "min") {
          return a.value < b.value ? 1 : -1;
        }
        if (a.threshold_type === "max") {
          return a.value > b.value ? 1 : -1;
        }
      }
      return 0;
    });
    thresholds.forEach((threshold) => {
      const calcExceedingThreshold = (value: number) => {
        if (threshold.threshold_type === "max") {
          return value > threshold.value;
        }
        if (threshold.threshold_type === "min") {
          return value < threshold.value;
        }
        return false;
      };

      const getColor = (kind: keyof typeof COLORS = "PLOTS") => {
        const colors = COLORS[kind];
        const hasColor = threshold.color && threshold.color in colors;
        if (threshold.threshold_type === "max") {
          return hasColor ? colors[threshold.color as keyof typeof colors] : colors.red;
        }
        if (threshold.threshold_type === "min") {
          return hasColor ? colors[threshold.color as keyof typeof colors] : colors.blue;
        }
      };

      data.lines?.forEach((line) => {
        const points = line.data?.datapoints;
        points?.forEach((point, index, pointArray) => {
          const prev = pointArray[index - 1];
          const exceedsThreshold = calcExceedingThreshold(point.value);
          const intersection = prev
            ? interpolateIntersection(
                +new Date(prev.timestamp),
                prev.value,
                +new Date(point.timestamp),
                point.value,
                threshold.value,
              )
            : +new Date(point.timestamp);

          // new plot
          if (!currentPlot && exceedsThreshold) {
            currentPlot = {
              color: getColor("PLOTS"),
              from: intersection,
            };
          }
          // same plot
          if (currentPlot && exceedsThreshold) {
            currentPlot.to = +new Date(point.timestamp);
          }
          // close plot
          if (currentPlot && !exceedsThreshold) {
            currentPlot.to = intersection;
            plots.push(currentPlot);
            currentPlot = undefined;
          }
        });

        // end of line - add remaining plot
        if (currentPlot) {
          plots.push(currentPlot);
          currentPlot = undefined;
        }
      });
    });

    return [plots, lines];
  }, [configuration.thresholds, data?.lines]);
};
